import React, { useMemo, useState, useEffect } from 'react';
import './App.css';
import { GoogleLogin, googleLogout } from '@react-oauth/google';
import RichTextEditor from './components/RichTextEditor';

function decodeGoogleCredential(credential) {
  if (!credential) return null;

  try {
    const payload = credential.split('.')[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = decodeURIComponent(
      atob(base64)
        .split('')
        .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    );
    return JSON.parse(decoded);
  } catch (error) {
    return null;
  }
}

function App({ googleClientIdConfigured }) {
  const [selectedPost, setSelectedPost] = useState(null);
  const [routeHash, setRouteHash] = useState('');
  const [publicPosts, setPublicPosts] = useState([]);
  const [adminUser, setAdminUser] = useState(null);
  const [authToken, setAuthToken] = useState('');
  const [authError, setAuthError] = useState('');
  const [apiMessage, setApiMessage] = useState('');
  const [posts, setPosts] = useState([]);
  const [savingPost, setSavingPost] = useState(false);
  const [editingSlug, setEditingSlug] = useState('');
  const [deletingSlug, setDeletingSlug] = useState('');
  const [publishingSlug, setPublishingSlug] = useState('');
  const [unpublishingSlug, setUnpublishingSlug] = useState('');
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [mediaMessage, setMediaMessage] = useState('');
  const [mediaMessageType, setMediaMessageType] = useState('');
  const [mediaFiles, setMediaFiles] = useState([]);
  const [postForm, setPostForm] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    status: 'draft'
  });

  const fetchPublicPosts = async () => {
    try {
      const response = await fetch('/api/posts/public');
      if (!response.ok) {
        setPublicPosts([]);
        return;
      }

      const data = await response.json();
      const apiPosts = Array.isArray(data.posts) ? data.posts : [];
      const sorted = apiPosts.sort((a, b) => String(b.publishedAt || b.date || '').localeCompare(String(a.publishedAt || a.date || '')));
      setPublicPosts(sorted);
    } catch (_error) {
      setPublicPosts([]);
    }
  };

  useEffect(() => {
    fetchPublicPosts();
  }, []);

  useEffect(() => {
    const updateRouteHash = () => {
      setRouteHash(window.location.hash || '#blog');
    };

    updateRouteHash();
    window.addEventListener('hashchange', updateRouteHash);

    return () => {
      window.removeEventListener('hashchange', updateRouteHash);
    };
  }, []);

  useEffect(() => {
    const hashValue = (routeHash || '#blog').replace(/^#/, '');

    if (!hashValue || hashValue === 'blog') {
      setSelectedPost(null);
      return;
    }

    if (hashValue.startsWith('blog/')) {
      const slug = hashValue.slice('blog/'.length);
      const publicPost = publicPosts.find((post) => post.slug === slug) || null;
      setSelectedPost(publicPost);
      return;
    }

    if (hashValue.startsWith('preview/')) {
      if (!adminUser) {
        setSelectedPost(null);
        return;
      }

      const slug = hashValue.slice('preview/'.length);
      const draftOrSavedPost = posts.find((post) => post.slug === slug) || null;
      setSelectedPost(draftOrSavedPost);
      return;
    }

    setSelectedPost(null);
  }, [routeHash, adminUser, publicPosts, posts]);

  const allowedEmails = useMemo(
    () =>
      (process.env.REACT_APP_ADMIN_ALLOWED_EMAILS || '')
        .split(',')
        .map((email) => email.trim().toLowerCase())
        .filter(Boolean),
    []
  );

  const handleGoogleSuccess = (credentialResponse) => {
    const profile = decodeGoogleCredential(credentialResponse.credential);

    if (!profile || !profile.email) {
      setAuthError('Could not read your Google profile. Please try again.');
      return;
    }

    if (!profile.email_verified) {
      setAuthError('Your Google email must be verified to sign in.');
      return;
    }

    const email = profile.email.toLowerCase();
    const isAllowed = allowedEmails.length > 0 && allowedEmails.includes(email);

    if (!isAllowed) {
      setAuthError('This account is not allowed for admin access.');
      return;
    }

    setAuthError('');
    setApiMessage('');
    setAuthToken(credentialResponse.credential || '');
    setAdminUser({
      email,
      name: profile.name || email,
      picture: profile.picture || ''
    });

    verifyAdminSession(credentialResponse.credential || '');
    fetchSavedPosts(credentialResponse.credential || '');
    fetchMediaLibrary(credentialResponse.credential || '');
  };

  const handleGoogleError = () => {
    setAuthError('Google sign-in failed. Please try again.');
  };

  const authHeaders = (token) => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  const verifyAdminSession = async (token) => {
    try {
      const response = await fetch('/api/admin/me', {
        headers: authHeaders(token)
      });

      const payload = await response.json();
      if (!response.ok) {
        setApiMessage(payload.error || 'Admin verification failed.');
        return;
      }

      setApiMessage(`Verified on server as ${payload.user.email}`);
    } catch (error) {
      setApiMessage('Could not reach CMS API.');
    }
  };

  const fetchMediaLibrary = async (token) => {
    if (!token) return;

    setLoadingMedia(true);

    try {
      const response = await fetch('/api/media', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const payload = await response.json();
      if (!response.ok) {
        setApiMessage(payload.error || 'Failed to load media library.');
        setLoadingMedia(false);
        return;
      }

      setMediaFiles(Array.isArray(payload.files) ? payload.files : []);
    } catch (_error) {
      setApiMessage('Could not load media library.');
    }

    setLoadingMedia(false);
  };

  const fetchSavedPosts = async (token) => {
    try {
      const response = await fetch('/api/posts', {
        headers: authHeaders(token)
      });

      const payload = await response.json();
      if (!response.ok) {
        setApiMessage(payload.error || 'Failed to load saved posts.');
        return;
      }

      setPosts(payload.posts || []);
    } catch (error) {
      setApiMessage('Could not load saved posts.');
    }
  };

  const handlePostInput = (event) => {
    const { name, value } = event.target;
    setPostForm((previous) => ({ ...previous, [name]: value }));
  };

  const resetPostForm = () => {
    setPostForm({ title: '', slug: '', excerpt: '', content: '', status: 'draft' });
    setEditingSlug('');
  };

  const handleEditPost = (post) => {
    setPostForm({
      title: post.title || '',
      slug: post.slug || '',
      excerpt: post.excerpt || '',
      content: post.content || '',
      status: post.status || 'draft'
    });
    setEditingSlug(post.slug || '');
    setApiMessage(`Editing post: ${post.slug}`);
  };

  const handleDeletePost = async (slug) => {
    if (!authToken) {
      setApiMessage('You are not authenticated for API calls.');
      return;
    }

    const confirmed = window.confirm(`Delete post "${slug}"? This cannot be undone.`);
    if (!confirmed) return;

    setDeletingSlug(slug);
    setApiMessage('');

    try {
      const response = await fetch(`/api/posts/${encodeURIComponent(slug)}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });

      const payload = await response.json();
      if (!response.ok) {
        setApiMessage(payload.error || 'Failed to delete post.');
        setDeletingSlug('');
        return;
      }

      if (editingSlug === slug) {
        resetPostForm();
      }

      setApiMessage(`Deleted post: ${slug}`);
      await fetchSavedPosts(authToken);
      await fetchPublicPosts();
    } catch (error) {
      setApiMessage('Failed to delete post.');
    }

    setDeletingSlug('');
  };

  const handleSavePost = async (event) => {
    event.preventDefault();

    if (!authToken) {
      setApiMessage('You are not authenticated for API calls.');
      return;
    }

    setSavingPost(true);
    setApiMessage('');

    try {
      const response = await fetch(
        editingSlug ? `/api/posts/${encodeURIComponent(editingSlug)}` : '/api/posts',
        {
          method: editingSlug ? 'PUT' : 'POST',
        headers: authHeaders(authToken),
        body: JSON.stringify(postForm)
        }
      );

      const payload = await response.json();
      if (!response.ok) {
        setApiMessage(payload.error || 'Failed to save post.');
        setSavingPost(false);
        return;
      }

      setApiMessage(`${editingSlug ? 'Updated' : 'Saved'} post: ${payload.post.slug}`);
      resetPostForm();
      await fetchSavedPosts(authToken);
      await fetchPublicPosts();
    } catch (error) {
      setApiMessage('Failed to save post.');
    }

    setSavingPost(false);
  };

  const handleMediaUpload = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      setMediaMessage('Select a file before uploading.');
      setMediaMessageType('error');
      return;
    }

    if (!authToken) {
      setMediaMessage('You are not authenticated for media uploads.');
      setMediaMessageType('error');
      event.target.value = '';
      return;
    }

    setUploadingMedia(true);
    setApiMessage('');
    setMediaMessage('');
    setMediaMessageType('');

    try {
      const data = new FormData();
      data.append('file', file);

      const response = await fetch('/api/media', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`
        },
        body: data
      });

      const payload = await response.json();
      if (!response.ok) {
        const message = payload.error || 'Media upload failed.';
        setApiMessage(message);
        setMediaMessage(message);
        setMediaMessageType('error');
        setUploadingMedia(false);
        return;
      }

      const message = `Uploaded media: ${payload.url}`;
      setApiMessage(message);
      setMediaMessage(message);
      setMediaMessageType('success');
      await fetchMediaLibrary(authToken);
    } catch (error) {
      setApiMessage('Media upload failed.');
      setMediaMessage('Media upload failed.');
      setMediaMessageType('error');
    }

    setUploadingMedia(false);
    event.target.value = '';
  };

  const handlePublishPost = async (slug) => {
    if (!authToken) {
      setApiMessage('You are not authenticated for API calls.');
      return;
    }

    setPublishingSlug(slug);
    setApiMessage('');

    try {
      const response = await fetch(`/api/posts/${encodeURIComponent(slug)}/publish`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });

      const payload = await response.json();
      if (!response.ok) {
        setApiMessage(payload.error || 'Failed to publish post.');
        setPublishingSlug('');
        return;
      }

      setApiMessage(`Published post: ${payload.post.slug}`);
      await fetchSavedPosts(authToken);
      await fetchPublicPosts();
    } catch (_error) {
      setApiMessage('Failed to publish post.');
    }

    setPublishingSlug('');
  };

  const handleUnpublishPost = async (slug) => {
    if (!authToken) {
      setApiMessage('You are not authenticated for API calls.');
      return;
    }

    setUnpublishingSlug(slug);
    setApiMessage('');

    try {
      const response = await fetch(`/api/posts/${encodeURIComponent(slug)}/unpublish`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });

      const payload = await response.json();
      if (!response.ok) {
        setApiMessage(payload.error || 'Failed to unpublish post.');
        setUnpublishingSlug('');
        return;
      }

      setApiMessage(`Unpublished post: ${payload.post.slug}`);
      await fetchSavedPosts(authToken);
      await fetchPublicPosts();
    } catch (_error) {
      setApiMessage('Failed to unpublish post.');
    }

    setUnpublishingSlug('');
  };

  const handlePreviewPost = (post) => {
    window.location.hash = `preview/${post.slug}`;
  };

  const handleOpenPublicPost = (post) => {
    window.location.hash = `blog/${post.slug}`;
  };

  const handleBackToBlog = () => {
    window.location.hash = 'blog';
  };

  const handleSignOut = () => {
    googleLogout();
    setAdminUser(null);
    setAuthToken('');
    setAuthError('');
    setApiMessage('');
    setPosts([]);
    setMediaFiles([]);

    const hashValue = (window.location.hash || '').replace(/^#/, '');
    if (hashValue.startsWith('preview/')) {
      window.location.hash = 'blog';
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>Madsen Dynamics</h1>
        <p>Premium Web Disasters</p>
        <nav>
          <a href="#blog">Blog</a>
          <a href="#admin">Admin</a>
        </nav>
      </header>
      
      <main>
        <section id="blog">
          <h2>Latest Blog Posts</h2>
          {selectedPost ? (
            <div className="blog-post-detail">
              <button className="back-button" onClick={handleBackToBlog}>← Back to Blog</button>
              <article>
                <h3>{selectedPost.title}</h3>
                {selectedPost.status === 'draft' ? <p className="draft-preview-banner">Draft preview (admin only)</p> : null}
                <time dateTime={selectedPost.date}>{new Date(selectedPost.date).toLocaleDateString()}</time>
                {/* content is server-rendered HTML from our own WordPress database */}
                <div className="post-content" dangerouslySetInnerHTML={{ __html: selectedPost.content }} />
              </article>
            </div>
          ) : (
            <div className="blog-posts-list">
              {publicPosts.map(post => (
                <article key={post.id} className="blog-post-card" onClick={() => handleOpenPublicPost(post)}>
                  <h3>{post.title}</h3>
                  <time dateTime={post.date}>{new Date(post.date).toLocaleDateString()}</time>
                  <div className="post-excerpt" dangerouslySetInnerHTML={{ __html: post.excerpt }} />
                  <button className="read-more">Read More →</button>
                </article>
              ))}
              {publicPosts.length === 0 ? <p>No published posts available.</p> : null}
            </div>
          )}
        </section>

        <section id="admin">
          <h2>Admin</h2>
          {!googleClientIdConfigured ? (
            <p className="auth-warning">
              Google sign-in is not configured. Set REACT_APP_GOOGLE_CLIENT_ID and
              REACT_APP_ADMIN_ALLOWED_EMAILS in your environment.
            </p>
          ) : adminUser ? (
            <div className="admin-session-wrap">
              <div className="admin-session">
                {adminUser.picture ? <img src={adminUser.picture} alt={adminUser.name} /> : null}
                <div>
                  <p className="admin-welcome">Signed in as {adminUser.name}</p>
                  <p className="admin-email">{adminUser.email}</p>
                </div>
                <button className="back-button" onClick={handleSignOut}>Sign out</button>
              </div>

              {apiMessage ? <p className="auth-warning">{apiMessage}</p> : null}

              <form className="admin-form" onSubmit={handleSavePost}>
                <h3>Create or Update Post</h3>
                <input
                  name="title"
                  placeholder="Title"
                  value={postForm.title}
                  onChange={handlePostInput}
                  required
                />
                <input
                  name="slug"
                  placeholder="Slug (optional, auto from title if empty)"
                  value={postForm.slug}
                  onChange={handlePostInput}
                />
                <input
                  name="excerpt"
                  placeholder="Excerpt"
                  value={postForm.excerpt}
                  onChange={handlePostInput}
                />
                <select name="status" value={postForm.status} onChange={handlePostInput}>
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
                <textarea
                  name="content"
                  placeholder="HTML content"
                  value={postForm.content}
                  readOnly
                  hidden
                />
                <RichTextEditor
                  value={postForm.content}
                  onChange={(html) =>
                    setPostForm((previous) => ({
                      ...previous,
                      content: html,
                    }))
                  }
                  placeholder="Write your post content here..."
                  mediaItems={mediaFiles}
                  onRefreshMedia={() => fetchMediaLibrary(authToken)}
                />
                <button className="read-more" type="submit" disabled={savingPost}>
                  {savingPost ? 'Saving...' : editingSlug ? 'Update Post' : 'Save Post'}
                </button>
                {editingSlug ? (
                  <button className="secondary-button" type="button" onClick={resetPostForm}>
                    Cancel Edit
                  </button>
                ) : null}
              </form>

              <div className="admin-form">
                <h3>Upload Media</h3>
                <input type="file" onChange={handleMediaUpload} disabled={uploadingMedia} />
                <p>{uploadingMedia ? 'Uploading...' : 'Upload an image/video for blog content.'}</p>
                {mediaMessage ? (
                  <p className={mediaMessageType === 'success' ? 'status-success' : 'status-error'}>{mediaMessage}</p>
                ) : null}
                <div className="media-library-summary">
                  <span>{loadingMedia ? 'Refreshing media library...' : `Media files available: ${mediaFiles.length}`}</span>
                  <button
                    className="secondary-button"
                    type="button"
                    onClick={() => fetchMediaLibrary(authToken)}
                    disabled={loadingMedia || !authToken}
                  >
                    Refresh Library
                  </button>
                </div>
              </div>

              <div className="admin-form">
                <h3>Saved Posts</h3>
                {posts.length === 0 ? (
                  <p>No saved CMS posts yet.</p>
                ) : (
                  <ul className="admin-post-list">
                    {posts.map((post) => (
                      <li key={post.slug}>
                        <span>
                          {post.slug} - {post.title}
                          <span className={`post-status-badge ${post.status === 'published' ? 'published' : 'draft'}`}>
                            {post.status || 'draft'}
                          </span>
                        </span>
                        <div className="post-actions">
                          <button className="secondary-button" type="button" onClick={() => handlePreviewPost(post)}>
                            Preview
                          </button>
                          <button className="secondary-button" type="button" onClick={() => handleEditPost(post)}>
                            Edit
                          </button>
                          {post.status === 'published' ? (
                            <button
                              className="secondary-button"
                              type="button"
                              onClick={() => handleUnpublishPost(post.slug)}
                              disabled={unpublishingSlug === post.slug}
                            >
                              {unpublishingSlug === post.slug ? 'Unpublishing...' : 'Unpublish'}
                            </button>
                          ) : (
                            <button
                              className="read-more"
                              type="button"
                              onClick={() => handlePublishPost(post.slug)}
                              disabled={publishingSlug === post.slug}
                            >
                              {publishingSlug === post.slug ? 'Publishing...' : 'Publish'}
                            </button>
                          )}
                          <button
                            className="danger-button"
                            type="button"
                            onClick={() => handleDeletePost(post.slug)}
                            disabled={deletingSlug === post.slug}
                          >
                            {deletingSlug === post.slug ? 'Deleting...' : 'Delete'}
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ) : (
            <div className="auth-panel">
              <p>Sign in with Google to unlock admin mode.</p>
              <GoogleLogin onSuccess={handleGoogleSuccess} onError={handleGoogleError} />
              {authError ? <p className="auth-error">{authError}</p> : null}
              {allowedEmails.length === 0 ? (
                <p className="auth-warning">
                  REACT_APP_ADMIN_ALLOWED_EMAILS is empty. No accounts are currently permitted.
                </p>
              ) : null}
            </div>
          )}
        </section>
        
      </main>
      
      <footer>
        <p>&copy; 2026 Madsen Dynamics. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default App;
